import DataLoader from 'dataloader'
import { GraphQLError } from 'graphql'

import config from '@acre/config'

import { Maybe, MiOrganisationInput, MiOrganisationResponse, Organisation, OrganisationInput, OrganisationResponse } from '../generated/resolvers'
import request from '../requesters/default'
import {
  CdmCreateOrganisationResponse,
  CdmGetOrganisationResponse,
  CdmUpdateOrganisationResponse,
} from '../service/luther'
import { UUID } from '../types'
import { formatOrganisationPayload, formatOrganisationResponse } from '../utils/schemaMapping/organisations'

const { CLIENT_PORTAL_API_URL, CLIENT_PORTAL_OATHKEEPER_API_URL } = config

type OrganisationLoaderInput = { id: UUID; useClientApi?: boolean; includeFinancialConfigFields?: boolean }

type OrganisationLoaderIdsParam = (string | OrganisationLoaderInput)[]

export enum OrgSourceType {
  Insurance = 'INSURANCE',
  Club = 'CLUB',
  Lender = 'LENDER',
  Insurer = 'INSURER'
}

const fetchOrganisationBatchFunction = async (ids: OrganisationLoaderIdsParam) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const actualIds = ids.map((id) => (typeof id === 'string' ? id : id.id))
  const includeFinancialConfigFields = ids.some((id) => typeof id !== 'string' && id.includeFinancialConfigFields)
  const axiosOpts = ids.some((id) => typeof id !== 'string' && id.useClientApi)
    ? useOathKeeper === 'true'
      ? { baseURL: CLIENT_PORTAL_OATHKEEPER_API_URL }
      : { baseURL: CLIENT_PORTAL_API_URL }
    : { params: { page_size: 1000 } }

  const url = `/organisation?organisation_ids=${actualIds.join('&organisation_ids=')}&organisation_details=true` +
    (includeFinancialConfigFields ? '&include_financial_configuration_fields=true' : '')

  const response = await request.get<CdmGetOrganisationResponse>(url, axiosOpts)

  return actualIds.map((id) => {
    const organisation = response.data.organisations?.find((org) => org.organisation_id === id)
    return organisation
      ? formatOrganisationResponse(organisation)
      : new GraphQLError(`No result for ${id}`, {
        extensions: {
          status: 404,
        },
      })
  })
}

const fetchOrganisationMIBatchFunction = async (ids: OrganisationLoaderIdsParam) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  const actualIds = ids.map((id) => (typeof id === 'string' ? id : id.id))
  const axiosOpts = ids.some((id) => typeof id !== 'string' && id.useClientApi)
    ? useOathKeeper === 'true'
      ? { baseURL: CLIENT_PORTAL_OATHKEEPER_API_URL }
      : { baseURL: CLIENT_PORTAL_API_URL }
    : {}

  try {
    const response = await request.get<MiOrganisationResponse>(
      '/mi/firm/organisations',
      {
        ...axiosOpts,
        params: {
          org_ids: actualIds,
          page_size: 1000,
          all_users: true,
        }
      }
    )

    return actualIds.map((id) => {
      const organisation = response.data.organisations?.find((org) => org.organisation_id === id)
      return organisation || null  // Return null instead of GraphQLError for missing orgs
    })
  } catch (error) {
    // If the API call fails, return null for all requested IDs
    console.error('Error fetching organisation MI:', error)
    return actualIds.map(() => null)
  }
}

export const OrganisationLoader = new DataLoader(
  (ids: OrganisationLoaderIdsParam) => fetchOrganisationBatchFunction(ids),
  { cacheKeyFn: (key) => (typeof key === 'string' ? key : JSON.stringify(key)), maxBatchSize: 30 },
)

export const OrganisationMILoader = new DataLoader(
  (ids: OrganisationLoaderIdsParam) => fetchOrganisationMIBatchFunction(ids),
  { cacheKeyFn: (key) => (typeof key === 'string' ? key : JSON.stringify(key)), maxBatchSize: 30 },
)

export const fetchOrganisation = async (id: UUID, useClientApi?: Maybe<boolean>, includeFinancialConfigFields?: Maybe<boolean>) => {
  return await OrganisationLoader.load({ id, useClientApi: useClientApi || false, includeFinancialConfigFields: includeFinancialConfigFields || false })
}

export const fetchOrganisationByExtId = async (id: string) => {
  const response = await request.get<CdmGetOrganisationResponse>('/organisation', {
    params: { organisation_ext_identifiers: id },
  })

  const organisation = response.data.organisations?.[0]

  if (organisation) {
    return formatOrganisationResponse(organisation)
  }

  return null
}

export const fetchOrganisationsByExtId = async (id: string) => {
  const response = await request.get<CdmGetOrganisationResponse>('/organisation', {
    params: { organisation_ext_identifiers: id },
  })

  const organisations = response.data.organisations
  let responseOrganisation: Organisation[] = []
  if (organisations) {
    organisations.map((org) => {
      responseOrganisation.push(formatOrganisationResponse(org))
    })

    return responseOrganisation
  }

  return null
}

export const fetchOrganisationsAdministeredBy = async (administeredBy: string): Promise<Organisation[]> => {
  const response = await request.get<CdmGetOrganisationResponse>('/organisation', {
    params: {
      filter_administered_by_org_id: administeredBy,
      page_size: 1000,
      organisation_details: true,
    },
  })

  if (response.data.organisations && response.data.organisations.length > 0) {
    return response.data.organisations.map(formatOrganisationResponse)
  }

  return []
}

export const fetchOrganisationsBySourceType = async (sourceType: OrgSourceType): Promise<Organisation[]> => {
  const response = await request.get('/local-sourcing/organisations', {
    params: {
      type: sourceType,
    },
  })

  if (response?.data?.organisations?.length > 0) {
    return response.data.organisations.map(formatOrganisationResponse)
  }

  return []
}

export const fetchOrganisationsRepresentativeOf = async (representativeOf: string) => {
  const BATCH_SIZE = 200
  const allOrganisations: Organisation[] = []
  let bookmark: string | undefined = undefined

  do {
    const rawResponse: { data: CdmGetOrganisationResponse } = await request.get<CdmGetOrganisationResponse>('/organisation', {
      params: {
        filter_representative_of_org_id: representativeOf,
        page_size: BATCH_SIZE,
        organisation_details: true,
        ...(bookmark ? { bookmark } : {}),
      },
    })

    if (rawResponse.data.organisations && rawResponse.data.organisations.length > 0) {
      allOrganisations.push(...rawResponse.data.organisations.map(formatOrganisationResponse))
    }

    bookmark = rawResponse.data.next_page?.bookmark
  } while (bookmark)

  const response: OrganisationResponse = {
    organisations: allOrganisations,
    next_page: undefined,
  }

  return response
}

export const fetchOrganisationsMi = async (params: MiOrganisationInput) => {
  const { page_size = 25, bookmark = 0, ...restParams } = params
  const rawResponse = await request.get<MiOrganisationResponse>('/mi/firm/organisations', {
    params: {
      ...restParams,
      page_size,
      bookmark,
    },
  })

  return rawResponse.data
}

export const updateOrganisation = async (id: UUID, input: OrganisationInput) => {
  const response = await request.patch<CdmUpdateOrganisationResponse>(
    `/organisation/${id}`,
    formatOrganisationPayload(input),
  )

  OrganisationLoader.clear({ id, useClientApi: false })

  return formatOrganisationResponse(response.data.organisation!)
}

export const createOrganisation = async (input: OrganisationInput) => {
  const response = await request.post<CdmCreateOrganisationResponse>(`/organisation`, formatOrganisationPayload(input))

  return formatOrganisationResponse(response.data.organisation!)
}
